/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/usePbxCliButtonConfigList.ts
 🗓️ Created: 2025-01-28
 🧩 Description:
    Custom hook for managing PBX CLI Button Configs list.
    Integrates with useDataGrid for pagination, sorting, and search.
────────────────────────────────────────────────────────────────*/

import { useCallback } from 'react';
import { useDataGrid, DataGridColumn } from '../../../hooks/useDataGrid';
import { fetchPbxCliButtonConfigs } from '../api';
import { PbxCliButtonConfig } from '../types';

/**
 * Custom hook for PBX CLI Button Configs list
 */
export function usePbxCliButtonConfigList(initialColumns?: DataGridColumn<PbxCliButtonConfig>[]) {
  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      sortBy: string;
      sortOrder: 'asc' | 'desc';
      search?: string;
    }) => {
      try {
        const result = await fetchPbxCliButtonConfigs(params);
        return {
          data: result.data || [],
          pagination: result.pagination || {
            page: 1,
            limit: 24,
            total: 0,
            totalPages: 1,
          },
        };
      } catch (error) {
        return {
          data: [] as PbxCliButtonConfig[],
          pagination: { page: 1, limit: 24, total: 0, totalPages: 0 },
        };
      }
    },
    [],
  );

  return useDataGrid({
    fetchData,
    initialColumns,
    initialSortBy: 'orderIndex',
    initialSortOrder: 'asc',
    initialLimit: 24,
    moduleName: 'pbxCliButtonConfigs',
  });
}

